import React, { CSSProperties, useMemo, useRef } from "react"

import { useEvent } from "shared/lib/useEvent"
import { TABLET_SIZE, useMediaQuery } from "shared/lib/useMediaQuery"

import { formatProjectPosition } from "entities/projects/lib/formatProjectPosition"
import { useProductPositionTableRowPayload } from "widgets/product-position-table/contexts/ProductPositionTableRowPayloadContext"
import { getProductPositionTableRoomColumnSizes } from "widgets/product-position-table/getProductPositionTableColumnSizes"

import { ProductPositionRowMenuCellHandle } from "../cells/ProductPositionRowMenuCell"
import { ProductPositionTableRoomRowModelResult } from "../room/hooks/useProductPositionTableRoomRowModel"

export const useProductPositionTableRoomRowViewModel = (
  props: ProductPositionTableRoomRowModelResult
) => {
  const isTablet = useMediaQuery(TABLET_SIZE)
  const { item, parentIndices, level, rowElementProps, onCollapse, actions } =
    props
  const {
    pendingProjectChildren,
    showClarificationRequestColumn,
    showSelectionCheckbox,
    columnPreset
  } = useProductPositionTableRowPayload()

  const rowRef = useRef<HTMLDivElement | null>(null)
  const menuCellRef = useRef<ProductPositionRowMenuCellHandle>(null)

  const style: CSSProperties = useMemo(
    () => ({
      ...rowElementProps?.style,
      ...getProductPositionTableRoomColumnSizes({
        level,
        showClarificationRequestColumn,
        hideMenu: isTablet,
        showSelectionCheckbox,
        columnPreset
      })
    }),
    [
      isTablet,
      level,
      rowElementProps?.style,
      showClarificationRequestColumn,
      showSelectionCheckbox,
      columnPreset
    ]
  )

  const isPending = !!pendingProjectChildren?.includes(item.id)

  const position = useMemo(
    () => formatProjectPosition(parentIndices),
    [parentIndices]
  )

  const handleCollapse = useEvent((e: React.MouseEvent) => {
    e.stopPropagation()
    onCollapse()
  })

  const handleKeyDown = useEvent((e: React.KeyboardEvent<HTMLDivElement>) => {
    rowElementProps?.onKeyDown?.(e)
    if (
      actions.length > 0 &&
      ((e.key === "F10" && e.shiftKey) || (e.key === "ArrowDown" && e.altKey))
    ) {
      e.preventDefault()
      menuCellRef.current?.openMenu()
    }
  })

  return {
    ...props,
    style,
    position,
    isPending,
    showClarificationRequestColumn,
    showSelectionCheckbox,
    columnPreset,
    canCollapse: (item.children?.length ?? 0) > 0,
    onCollapse: handleCollapse,
    actions,
    rowRef,
    menuCellRef,
    rowElementProps: rowElementProps
      ? {
          ...rowElementProps,
          onKeyDown: handleKeyDown
        }
      : rowElementProps
  }
}
